import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { invalidateSiteSettingsCache } from "@/hooks/useSiteSettings";
import { useAuth } from "@/contexts/AuthContext";
import { useAdmin } from "@/hooks/useAdmin";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { useToast } from "@/hooks/use-toast";
import { Save, MessageSquare, KeyRound, Phone, CreditCard, Sparkles, Code, Camera, Instagram, ScanEye, ChevronDown, Settings2, Mail, Megaphone, Lightbulb, Trash2, CheckCircle, Clock, XCircle, Wallet, Receipt, Banknote, Smartphone, CalendarOff, Loader2, Star, Bell } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { cn } from "@/lib/utils";
import BeholdFeed from "@/components/BeholdFeed";
import { LookbookManagerCard } from "@/components/admin/LookbookManagerCard";
import { WalkInCheckoutCard } from "@/components/admin/WalkInCheckoutCard";
import { ResendNotificationsCard } from "@/components/admin/ResendNotificationsCard";
import { useAvailabilityData, AvailabilityHoursEditor } from "@/components/AvailabilityManager";


const AdminSettings = () => {
  const { user } = useAuth();
  const { isSuperAdmin } = useAdmin();
  const location = useLocation();

  // Smooth-scroll to a section when navigated with #hash (e.g. /admin/settings#payment-portal)
  useEffect(() => {
    if (!location.hash) return;
    const id = location.hash.slice(1);
    const tryScroll = (attempt = 0) => {
      const el = document.getElementById(id);
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "start" });
      } else if (attempt < 20) {
        setTimeout(() => tryScroll(attempt + 1), 100);
      }
    };
    tryScroll();
  }, [location.hash, location.pathname]);

  const [instagramHandle, setInstagramHandle] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [address, setAddress] = useState("");
  const [smsConfirmEnabled, setSmsConfirmEnabled] = useState(false);
  const [emailReminderEnabled, setEmailReminderEnabled] = useState(false);
  const [reminderLeadMinutes, setReminderLeadMinutes] = useState("1440");
  const [reminderLeadMinutes2, setReminderLeadMinutes2] = useState("60");
  const [reminder2Enabled, setReminder2Enabled] = useState(true);
  const [outlookSyncEnabled, setOutlookSyncEnabled] = useState(false);
  const [outlookSyncEmail, setOutlookSyncEmail] = useState("");
  const [outlookBulkSyncing, setOutlookBulkSyncing] = useState(false);
  const [outlookBulkProgress, setOutlookBulkProgress] = useState<{ done: number; total: number } | null>(null);
  const [phoneVerificationEnabled, setPhoneVerificationEnabled] = useState(true);
  const [phoneRequired, setPhoneRequired] = useState(true);
  const [depositEnabled, setDepositEnabled] = useState(true);
  const [depositAmount, setDepositAmount] = useState("15");
  const [depositType, setDepositType] = useState("fixed");
  const [liquidGlassEnabled, setLiquidGlassEnabled] = useState(false);
  const [shimmerSpeed, setShimmerSpeed] = useState("2");
  const [shimmerDirection, setShimmerDirection] = useState("vertical");
  const [beholdFeedId, setBeholdFeedId] = useState("");
  const [snapwidgetEmbedId, setSnapwidgetEmbedId] = useState("1118656");
  const [snapwidgetMobileWidth, setSnapwidgetMobileWidth] = useState("440");
  const [snapwidgetMobileHeight, setSnapwidgetMobileHeight] = useState("560");
  const [snapwidgetTabletWidth, setSnapwidgetTabletWidth] = useState("768");
  const [snapwidgetTabletHeight, setSnapwidgetTabletHeight] = useState("560");
  const [snapwidgetDesktopHeight, setSnapwidgetDesktopHeight] = useState("600");
  const [googleReviewUrl, setGoogleReviewUrl] = useState("");
  const [googleReviewsEnabled, setGoogleReviewsEnabled] = useState(false);
  const [googleConnecting, setGoogleConnecting] = useState(false);
  const [requirePhotosFirst, setRequirePhotosFirst] = useState(false);
  const [requirePhotosSubsequent, setRequirePhotosSubsequent] = useState(false);
  const [requirePedicurePhoto, setRequirePedicurePhoto] = useState(false);
  const [requireCompletionPhoto, setRequireCompletionPhoto] = useState(false);
  const [photoEnforcementMode, setPhotoEnforcementMode] = useState<"block" | "warn">("warn");
  const [instagramAutoApprove, setInstagramAutoApprove] = useState(false);
  const [couponExpiryReminderEnabled, setCouponExpiryReminderEnabled] = useState(false);
  const [couponExpiryReminderDays, setCouponExpiryReminderDays] = useState("30");
  const [promoBannerEnabled, setPromoBannerEnabled] = useState(true);
  const [promoBannerTitle, setPromoBannerTitle] = useState("New Suite Special!");
  const [promoBannerSubtitle, setPromoBannerSubtitle] = useState("Any Medium or Long fullset");
  const [promoBannerDetail, setPromoBannerDetail] = useState("1 hour 30 minutes @ $75.00");
  const [promoBannerLink, setPromoBannerLink] = useState("/book");
  const [cameraMlDetection, setCameraMlDetection] = useState(true);
  const [emailConfirmationEnabled, setEmailConfirmationEnabled] = useState(true);
  const [igRequiredHashtag, setIgRequiredHashtag] = useState("#Inparadisebeauty");
  const [smsMarketingEnabled, setSmsMarketingEnabled] = useState(false);
  const [autoBlockEnabled, setAutoBlockEnabled] = useState(true);
  const [checkInEnabled, setCheckInEnabled] = useState(false);
  const [autoBlockUnlockDay, setAutoBlockUnlockDay] = useState("22");
  const [autoBlockRunning, setAutoBlockRunning] = useState<"none" | "block" | "unblock" | "auto">("none");
  const [allowAdminDeletePastBookings, setAllowAdminDeletePastBookings] = useState(false);
  const [bannerExcludeTestAccounts, setBannerExcludeTestAccounts] = useState(true);
  const [bannerExcludeInactiveBookings, setBannerExcludeInactiveBookings] = useState(true);
  const [autoBlockStatus, setAutoBlockStatus] = useState<{
    blockedCount: number;
    daysInNextMonth: number;
    monthLabel: string;
  } | null>(null);
  const [newEmail, setNewEmail] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [credentialsSaving, setCredentialsSaving] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [testSmsPhone, setTestSmsPhone] = useState("");
  const [testSmsType, setTestSmsType] = useState<"confirmation" | "cancellation" | "completion">("confirmation");
  const [testSmsSending, setTestSmsSending] = useState(false);
  const [testSmsResult, setTestSmsResult] = useState<{ sid: string; status: string; phone: string; errorMessage?: string } | null>(null);
  const [featureRequests, setFeatureRequests] = useState<any[]>([]);
  const [featureProfiles, setFeatureProfiles] = useState<Record<string, string>>({});
  // Payment portal
  const ALL_METHODS: { value: string; label: string; icon: any }[] = [
    { value: "cash", label: "Cash", icon: Banknote },
    { value: "card", label: "Card Terminal", icon: CreditCard },
    { value: "tap_to_pay", label: "Tap to Pay", icon: Smartphone },
    { value: "zelle", label: "Zelle", icon: Smartphone },
    { value: "venmo", label: "Venmo", icon: Smartphone },
    { value: "cashapp", label: "Cash App", icon: Smartphone },
    { value: "other", label: "Other", icon: Smartphone },
  ];
  const [acceptedMethods, setAcceptedMethods] = useState<string[]>([
    "cash", "card", "tap_to_pay", "zelle", "venmo", "cashapp", "other",
  ]);
  const [tipPresets, setTipPresets] = useState<string>("15,20,25");
  const [taxRate, setTaxRate] = useState<string>("0");
  const [recentManualPayments, setRecentManualPayments] = useState<any[]>([]);
  const [manualPayProfiles, setManualPayProfiles] = useState<Record<string, string>>({});
  const { toast } = useToast();

  useEffect(() => {
    const fetchSettings = async () => {
      const { data } = await supabase.from("site_settings").select("*");
      if (data) {
        data.forEach((s: any) => {
          if (s.key === "instagram_handle") setInstagramHandle(s.value);
          if (s.key === "phone") setPhone(s.value);
          if (s.key === "email") setEmail(s.value);
          if (s.key === "address") setAddress(s.value);
          if (s.key === "sms_confirm_enabled") setSmsConfirmEnabled(s.value === "true");
          if (s.key === "email_reminder_enabled") setEmailReminderEnabled(s.value === "true");
          if (s.key === "reminder_lead_minutes") setReminderLeadMinutes(s.value || "1440");
          if (s.key === "reminder_lead_minutes_2") setReminderLeadMinutes2(s.value || "60");
          if (s.key === "reminder_2_enabled") setReminder2Enabled(s.value !== "false");
          if (s.key === "outlook_sync_enabled") setOutlookSyncEnabled(s.value === "true");
          if (s.key === "outlook_sync_email") setOutlookSyncEmail(s.value || "");
          if (s.key === "phone_verification_enabled") setPhoneVerificationEnabled(s.value !== "false");
          if (s.key === "phone_required") setPhoneRequired(s.value !== "false");
          if (s.key === "deposit_enabled") setDepositEnabled(s.value !== "false");
          if (s.key === "deposit_amount") setDepositAmount(s.value);
          if (s.key === "deposit_type") setDepositType(s.value);
          if (s.key === "liquid_glass_enabled") setLiquidGlassEnabled(s.value === "true");
          if (s.key === "shimmer_speed") setShimmerSpeed(s.value || "2");
          if (s.key === "shimmer_direction") setShimmerDirection(s.value || "vertical");
          if (s.key === "behold_feed_id") setBeholdFeedId(s.value || "");
          if (s.key === "snapwidget_embed_id") setSnapwidgetEmbedId(s.value || "1118656");
          if (s.key === "snapwidget_mobile_width") setSnapwidgetMobileWidth(s.value || "440");
          if (s.key === "snapwidget_mobile_height") setSnapwidgetMobileHeight(s.value || "560");
          if (s.key === "snapwidget_tablet_width") setSnapwidgetTabletWidth(s.value || "768");
          if (s.key === "snapwidget_tablet_height") setSnapwidgetTabletHeight(s.value || "560");
          if (s.key === "snapwidget_desktop_height") setSnapwidgetDesktopHeight(s.value || "600");
          if (s.key === "google_review_url") setGoogleReviewUrl(s.value || "");
          if (s.key === "google_reviews_enabled") setGoogleReviewsEnabled(s.value === "true");
          if (s.key === "require_photos_first") setRequirePhotosFirst(s.value === "true");
          if (s.key === "require_photos_subsequent") setRequirePhotosSubsequent(s.value === "true");
          if (s.key === "require_pedicure_photo") setRequirePedicurePhoto(s.value === "true");
          if (s.key === "require_completion_photo") setRequireCompletionPhoto(s.value === "true");
          if (s.key === "photo_enforcement_mode") setPhotoEnforcementMode(s.value === "block" ? "block" : "warn");
          if (s.key === "instagram_auto_approve") setInstagramAutoApprove(s.value === "true");
          if (s.key === "coupon_expiry_reminder_enabled") setCouponExpiryReminderEnabled(s.value === "true");
          if (s.key === "coupon_expiry_reminder_days") setCouponExpiryReminderDays(s.value || "30");
          if (s.key === "camera_ml_detection") setCameraMlDetection(s.value !== "false");
          if (s.key === "email_confirmation_enabled") setEmailConfirmationEnabled(s.value !== "false");
          if (s.key === "ig_required_hashtag") setIgRequiredHashtag(s.value || "#Inparadisebeauty");
          if (s.key === "sms_marketing_enabled") setSmsMarketingEnabled(s.value === "true");
          if (s.key === "auto_block_enabled") setAutoBlockEnabled(s.value !== "false");
          if (s.key === "check_in_enabled") setCheckInEnabled(s.value === "true");
          if (s.key === "auto_block_unlock_day") setAutoBlockUnlockDay(s.value || "22");
          if (s.key === "allow_admin_delete_past_bookings") setAllowAdminDeletePastBookings(s.value === "true");
          if (s.key === "banner_exclude_test_accounts") setBannerExcludeTestAccounts(s.value !== "false");
          if (s.key === "banner_exclude_inactive_bookings") setBannerExcludeInactiveBookings(s.value !== "false");
          if (s.key === "promo_banner_enabled") setPromoBannerEnabled(s.value === "true");
          if (s.key === "promo_banner_title") setPromoBannerTitle(s.value || "");
          if (s.key === "promo_banner_subtitle") setPromoBannerSubtitle(s.value || "");
          if (s.key === "promo_banner_detail") setPromoBannerDetail(s.value || "");
          if (s.key === "promo_banner_link") setPromoBannerLink(s.value || "/book");
          if (s.key === "payment_accepted_methods") {
            try {
              const arr = JSON.parse(s.value);
              if (Array.isArray(arr) && arr.length > 0) setAcceptedMethods(arr);
            } catch {
              const arr = String(s.value).split(",").map((x) => x.trim()).filter(Boolean);
              if (arr.length) setAcceptedMethods(arr);
            }
          }
          if (s.key === "payment_tip_presets") {
            try {
              const arr = JSON.parse(s.value);
              if (Array.isArray(arr)) setTipPresets(arr.join(","));
            } catch {
              setTipPresets(s.value || "15,20,25");
            }
          }
          if (s.key === "payment_tax_rate") setTaxRate(s.value || "0");
        });
      }
      setLoading(false);
    };
    fetchSettings();
  }, []);

  // Fetch recent manual payments for the portal log
  useEffect(() => {
    const fetchManualPayments = async () => {
      const { data } = await supabase
        .from("manual_payments")
        .select("*")
        .order("created_at", { ascending: false })
        .limit(20);
      setRecentManualPayments(data || []);
      const userIds = [...new Set((data || []).map((p: any) => p.user_id))];
      if (userIds.length > 0) {
        const { data: profs } = await supabase
          .from("profiles")
          .select("user_id, full_name, email")
          .in("user_id", userIds);
        const map: Record<string, string> = {};
        (profs || []).forEach((p: any) => { map[p.user_id] = p.full_name || p.email || "Unknown"; });
        setManualPayProfiles(map);
      }
    };
    fetchManualPayments();
  }, []);

  // Fetch feature requests for super admin
  useEffect(() => {
    if (!isSuperAdmin) return;
    const fetchRequests = async () => {
      const { data } = await supabase
        .from("feature_requests")
        .select("*")
        .order("created_at", { ascending: false });
      setFeatureRequests(data || []);
      const userIds = [...new Set((data || []).map((r: any) => r.user_id))];
      if (userIds.length > 0) {
        const { data: profiles } = await supabase
          .from("profiles")
          .select("user_id, full_name, email")
          .in("user_id", userIds);
        const map: Record<string, string> = {};
        profiles?.forEach((p: any) => { map[p.user_id] = p.full_name || p.email || "Unknown"; });
        setFeatureProfiles(map);
      }
    };
    fetchRequests();
  }, [isSuperAdmin]);

  const updateRequestStatus = async (id: string, status: string) => {
    await supabase.from("feature_requests").update({ status, updated_at: new Date().toISOString() }).eq("id", id);
    setFeatureRequests((prev) => prev.map((r) => r.id === id ? { ...r, status } : r));
  };

  const deleteRequest = async (id: string) => {
    await supabase.from("feature_requests").delete().eq("id", id);
    setFeatureRequests((prev) => prev.filter((r) => r.id !== id));
  };

  const deleteManualPayment = async (id: string) => {
    if (!confirm("Delete this payment record? This cannot be undone.")) return;
    const { error } = await supabase.from("manual_payments").delete().eq("id", id);
    if (error) {
      toast({ title: "Error", description: error.message, variant: "destructive" });
      return;
    }
    setRecentManualPayments((prev) => prev.filter((p) => p.id !== id));
    toast({ title: "Payment deleted", description: "Record removed from log." });
  };

  const upsertSetting = async (key: string, value: string) => {
    const { data: existing } = await supabase
      .from("site_settings")
      .select("id")
      .eq("key", key)
      .maybeSingle();

    if (existing) {
      await supabase.from("site_settings").update({ value, updated_at: new Date().toISOString() }).eq("key", key);
    } else {
      await supabase.from("site_settings").insert({ key, value });
    }
  };

  const handleBulkOutlookSync = async () => {
    if (!outlookSyncEnabled || !outlookSyncEmail.trim()) {
      toast({
        title: "Enable Outlook sync first",
        description: "Turn on the toggle, enter an email, and save before syncing existing bookings.",
        variant: "destructive",
      });
      return;
    }
    setOutlookBulkSyncing(true);
    setOutlookBulkProgress({ done: 0, total: 0 });
    try {
      const today = new Date().toISOString().split("T")[0];
      const { data: bookings, error } = await supabase
        .from("bookings")
        .select("id, service_name, service_category, booking_date, booking_time, duration, status, notes, user_id").is("deleted_at", null)
        .gte("booking_date", today)
        .neq("status", "cancelled")
        .order("booking_date", { ascending: true });
      if (error) throw error;
      const list = bookings || [];
      if (list.length === 0) {
        toast({ title: "No upcoming bookings", description: "Nothing to sync." });
        return;
      }

      const userIds = Array.from(new Set(list.map((b) => b.user_id)));
      const { data: profileRows } = await supabase
        .from("profiles")
        .select("user_id, full_name, email, phone")
        .in("user_id", userIds);
      const profileMap: Record<string, { full_name?: string | null; email?: string | null; phone?: string | null }> = {};
      (profileRows || []).forEach((p: any) => { profileMap[p.user_id] = p; });

      setOutlookBulkProgress({ done: 0, total: list.length });
      const { syncBookingToOutlook } = await import("@/lib/outlookSync");
      let success = 0;
      let failed = 0;
      for (let i = 0; i < list.length; i++) {
        const b = list[i];
        const p = profileMap[b.user_id] || {};
        try {
          await syncBookingToOutlook(
            {
              id: b.id,
              service_name: b.service_name,
              service_category: b.service_category,
              booking_date: b.booking_date,
              booking_time: b.booking_time,
              duration: b.duration,
              status: b.status,
              notes: b.notes,
              client_name: p.full_name || undefined,
              client_email: p.email,
              client_phone: p.phone,
            },
            "updated",
          );
          success++;
        } catch {
          failed++;
        }
        setOutlookBulkProgress({ done: i + 1, total: list.length });
      }
      toast({
        title: "Outlook sync complete",
        description: `Sent ${success} invite${success === 1 ? "" : "s"}${failed ? ` · ${failed} failed` : ""}. Check your Outlook inbox.`,
      });
    } catch (err: any) {
      toast({ title: "Sync failed", description: err?.message || "Unknown error", variant: "destructive" });
    } finally {
      setOutlookBulkSyncing(false);
      setOutlookBulkProgress(null);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await Promise.all([
        upsertSetting("instagram_handle", instagramHandle),
        upsertSetting("phone", phone),
        upsertSetting("email", email),
        upsertSetting("address", address),
        upsertSetting("sms_confirm_enabled", smsConfirmEnabled ? "true" : "false"),
        upsertSetting("email_reminder_enabled", emailReminderEnabled ? "true" : "false"),
        upsertSetting("reminder_lead_minutes", String(Math.max(5, Math.min(1440, parseInt(reminderLeadMinutes, 10) || 1440)))),
        upsertSetting("reminder_lead_minutes_2", String(Math.max(5, Math.min(1440, parseInt(reminderLeadMinutes2, 10) || 60)))),
        upsertSetting("reminder_2_enabled", reminder2Enabled ? "true" : "false"),
        upsertSetting("outlook_sync_enabled", outlookSyncEnabled ? "true" : "false"),
        upsertSetting("outlook_sync_email", outlookSyncEmail.trim()),
        upsertSetting("phone_verification_enabled", phoneVerificationEnabled ? "true" : "false"),
        upsertSetting("phone_required", phoneRequired ? "true" : "false"),
        upsertSetting("deposit_enabled", depositEnabled ? "true" : "false"),
        upsertSetting("deposit_amount", depositEnabled ? depositAmount : "0"),
        upsertSetting("deposit_type", depositType),
        upsertSetting("liquid_glass_enabled", liquidGlassEnabled ? "true" : "false"),
        upsertSetting("shimmer_speed", shimmerSpeed),
        upsertSetting("shimmer_direction", shimmerDirection),
        upsertSetting("behold_feed_id", beholdFeedId),
        upsertSetting("snapwidget_embed_id", snapwidgetEmbedId),
        upsertSetting("snapwidget_mobile_width", snapwidgetMobileWidth),
        upsertSetting("snapwidget_mobile_height", snapwidgetMobileHeight),
        upsertSetting("snapwidget_tablet_width", snapwidgetTabletWidth),
        upsertSetting("snapwidget_tablet_height", snapwidgetTabletHeight),
        upsertSetting("snapwidget_desktop_height", snapwidgetDesktopHeight),
        upsertSetting("google_review_url", googleReviewUrl.trim()),
        upsertSetting("google_reviews_enabled", googleReviewsEnabled ? "true" : "false"),
        upsertSetting("require_photos_first", requirePhotosFirst ? "true" : "false"),
        upsertSetting("require_photos_subsequent", requirePhotosSubsequent ? "true" : "false"),
        upsertSetting("require_pedicure_photo", requirePedicurePhoto ? "true" : "false"),
        upsertSetting("require_completion_photo", requireCompletionPhoto ? "true" : "false"),
        upsertSetting("photo_enforcement_mode", photoEnforcementMode),
        upsertSetting("instagram_auto_approve", instagramAutoApprove ? "true" : "false"),
        upsertSetting("coupon_expiry_reminder_enabled", couponExpiryReminderEnabled ? "true" : "false"),
        upsertSetting("coupon_expiry_reminder_days", couponExpiryReminderDays),
        upsertSetting("camera_ml_detection", cameraMlDetection ? "true" : "false"),
        upsertSetting("email_confirmation_enabled", emailConfirmationEnabled ? "true" : "false"),
        upsertSetting("promo_banner_enabled", promoBannerEnabled ? "true" : "false"),
        upsertSetting("promo_banner_title", promoBannerTitle),
        upsertSetting("promo_banner_subtitle", promoBannerSubtitle),
        upsertSetting("promo_banner_detail", promoBannerDetail),
        upsertSetting("promo_banner_link", promoBannerLink),
        upsertSetting("ig_required_hashtag", igRequiredHashtag),
        upsertSetting("sms_marketing_enabled", smsMarketingEnabled ? "true" : "false"),
        upsertSetting("auto_block_enabled", autoBlockEnabled ? "true" : "false"),
        upsertSetting("check_in_enabled", checkInEnabled ? "true" : "false"),
        upsertSetting("auto_block_unlock_day", String(Math.max(1, Math.min(28, parseInt(autoBlockUnlockDay, 10) || 22)))),
        upsertSetting("allow_admin_delete_past_bookings", allowAdminDeletePastBookings ? "true" : "false"),
        upsertSetting("banner_exclude_test_accounts", bannerExcludeTestAccounts ? "true" : "false"),
        upsertSetting("banner_exclude_inactive_bookings", bannerExcludeInactiveBookings ? "true" : "false"),
        upsertSetting("payment_accepted_methods", JSON.stringify(acceptedMethods)),
        upsertSetting(
          "payment_tip_presets",
          JSON.stringify(
            tipPresets.split(",").map((x) => Number(x.trim())).filter((n) => !isNaN(n) && n >= 0 && n <= 100)
          )
        ),
        upsertSetting("payment_tax_rate", String(Number(taxRate) || 0)),
      ]);
      invalidateSiteSettingsCache();
      toast({ title: "Saved", description: "Settings updated successfully" });
    } catch (err: any) {
      toast({ title: "Error", description: err.message, variant: "destructive" });
    }
    setSaving(false);
  };

  const fetchAutoBlockStatus = async () => {
    try {
      const today = new Date();
      const month = today.getMonth();
      const year = today.getFullYear();
      const nextMonth = month === 11 ? 0 : month + 1;
      const nextMonthYear = month === 11 ? year + 1 : year;
      const pad = (n: number) => String(n).padStart(2, "0");
      const firstOfNext = `${nextMonthYear}-${pad(nextMonth + 1)}-01`;
      const daysInNextMonth = new Date(nextMonthYear, nextMonth + 1, 0).getDate();
      const lastOfNext = `${nextMonthYear}-${pad(nextMonth + 1)}-${pad(daysInNextMonth)}`;
      const MONTH_NAMES = [
        "January", "February", "March", "April", "May", "June",
        "July", "August", "September", "October", "November", "December",
      ];
      const { count } = await supabase
        .from("blocked_dates")
        .select("id", { count: "exact", head: true })
        .gte("blocked_date", firstOfNext)
        .lte("blocked_date", lastOfNext)
        .like("reason", "AUTO_BLOCK:%");
      setAutoBlockStatus({
        blockedCount: count ?? 0,
        daysInNextMonth,
        monthLabel: `${MONTH_NAMES[nextMonth]} ${nextMonthYear}`,
      });
    } catch {
      setAutoBlockStatus(null);
    }
  };

  useEffect(() => {
    fetchAutoBlockStatus();
  }, []);

  const runAutoBlock = async (mode: "block" | "unblock" | "auto") => {
    setAutoBlockRunning(mode);
    try {
      // Save the unlock day first so the function uses the latest value
      await upsertSetting(
        "auto_block_unlock_day",
        String(Math.max(1, Math.min(28, parseInt(autoBlockUnlockDay, 10) || 22))),
      );
      await upsertSetting("auto_block_enabled", autoBlockEnabled ? "true" : "false");

      const { data, error } = await supabase.functions.invoke("auto-block-next-month", {
        body: { mode, force: mode !== "auto" },
      });
      if (error) throw error;
      const action = (data as any)?.action ?? mode;
      const inserted = (data as any)?.inserted ?? 0;
      const deleted = (data as any)?.deleted ?? 0;
      toast({
        title: action === "unblock" ? "Next month unblocked" : "Next month blocked",
        description: `Added ${inserted} blocks, removed ${deleted}.`,
      });
      await fetchAutoBlockStatus();
    } catch (err: any) {
      toast({ title: "Error", description: err.message, variant: "destructive" });
    }
    setAutoBlockRunning("none");
  };

  if (loading) {
    return <div className="text-muted-foreground text-sm">Loading settings…</div>;
  }

  return (
    <div className="space-y-6 no-shimmer px-1 sm:px-0">
      <div>
        <h1 className="font-serif text-2xl sm:text-3xl font-bold italic">Settings</h1>
        <p className="text-muted-foreground text-sm mt-1">Manage site-wide settings</p>
      </div>

      <Card className="bg-card border-border/50 max-w-xl">
        <CardHeader>
          <CardTitle className="text-sm font-light tracking-[0.3em] uppercase text-muted-foreground">
            Business Info
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label className="text-xs tracking-wide uppercase text-muted-foreground">Instagram Handle</Label>
            <Input
              value={instagramHandle}
              onChange={(e) => setInstagramHandle(e.target.value)}
              placeholder="@paridisebeautyinc_"
              className="bg-background border-border/50 mt-1"
            />
          </div>
          <div>
            <Label className="text-xs tracking-wide uppercase text-muted-foreground">Phone Number</Label>
            <Input
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="(555) 123-4567"
              className="bg-background border-border/50 mt-1"
            />
          </div>
          <div>
            <Label className="text-xs tracking-wide uppercase text-muted-foreground">Email</Label>
            <Input
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="bookings@inparadisebeauty.com"
              className="bg-background border-border/50 mt-1"
            />
          </div>
          <div>
            <Label className="text-xs tracking-wide uppercase text-muted-foreground">Address</Label>
            <Input
              value={address}
              onChange={(e) => setAddress(e.target.value)}
              placeholder="123 Beauty Blvd, Suite 100"
              className="bg-background border-border/50 mt-1"
            />
          </div>
          <div className="pt-2 border-t border-border/50">
            <Label className="text-xs tracking-wide uppercase text-muted-foreground">Business Hours</Label>
            <p className="text-xs text-muted-foreground font-light mt-1 mb-3">
              Set the days and times you're open. These hours appear across the site (footer, contact pages) and control which slots clients can book.
            </p>
            <BusinessHoursEditorButton />
          </div>
          <Button onClick={handleSave} disabled={saving} className="mt-4 text-xs tracking-widest uppercase">
            <Save size={14} className="mr-2" />
            {saving ? "Saving…" : "Save Settings"}
          </Button>
        </CardContent>
      </Card>

      {/* Google Reviews */}
      <Card className="bg-card border-border/50 max-w-xl">
        <CardHeader>
          <CardTitle className="text-sm font-light tracking-[0.3em] uppercase text-muted-foreground flex items-center gap-2">
            <Star size={14} /> Google Reviews
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-5">
          <div className="flex items-start justify-between gap-4 p-4 border border-border/50 bg-background">
            <div className="flex-1">
              <Label className="text-sm font-medium">Enable Google Review CTAs</Label>
              <p className="text-xs text-muted-foreground font-light mt-1">
                When on, the Google review link is shown in the post-payment popup, payment receipts, completion emails, and the in-app review page.
                When off, only your in-app review form is shown.
              </p>
            </div>
            <Switch checked={googleReviewsEnabled} onCheckedChange={setGoogleReviewsEnabled} />
          </div>

          <div>
            <Label className="text-xs tracking-wide uppercase text-muted-foreground">Google Review URL</Label>
            <Input
              value={googleReviewUrl}
              onChange={(e) => setGoogleReviewUrl(e.target.value)}
              placeholder="https://g.page/r/..."
              className="bg-background border-border/50 mt-1"
              disabled={!googleReviewsEnabled}
            />
            <p className="text-xs text-muted-foreground mt-1 font-light">
              Paste the public review link from your Google Business Profile.
            </p>
          </div>

          <div className="pt-2 border-t border-border/50">
            <Label className="text-xs tracking-wide uppercase text-muted-foreground">Connect Google Account</Label>
            <p className="text-xs text-muted-foreground font-light mt-1 mb-3">
              Link a Google account so the salon can later auto-pull Google reviews and business info.
              (Read access only — coming soon.)
            </p>
            <Button
              type="button"
              variant="outline"
              disabled={googleConnecting}
              onClick={async () => {
                setGoogleConnecting(true);
                try {
                  const { lovable } = await import("@/integrations/lovable");
                  const result = await lovable.auth.signInWithOAuth("google", {
                    redirect_uri: window.location.origin + "/admin/settings",
                  });
                  if (result.error) {
                    toast({
                      title: "Couldn't connect Google",
                      description: (result.error as any)?.message || "Please try again.",
                      variant: "destructive",
                    });
                  }
                  // If redirected, the browser will navigate away; nothing more to do.
                } catch (err: any) {
                  toast({ title: "Error", description: err?.message || "Unknown error", variant: "destructive" });
                } finally {
                  setGoogleConnecting(false);
                }
              }}
              className="rounded-none text-xs tracking-widest uppercase border-primary/40 hover:bg-primary/5"
            >
              {googleConnecting ? (
                <><Loader2 size={14} className="mr-2 animate-spin" /> Connecting…</>
              ) : (
                <><Star size={14} className="mr-2" /> Connect Google Account</>
              )}
            </Button>
          </div>

          <Button onClick={handleSave} disabled={saving} className="mt-2 text-xs tracking-widest uppercase">
            <Save size={14} className="mr-2" />
            {saving ? "Saving…" : "Save Google Settings"}
          </Button>
        </CardContent>
      </Card>

      <Card className="bg-card border-border/50 max-w-xl">
        <CardHeader>
          <CardTitle className="text-sm font-light tracking-[0.3em] uppercase text-muted-foreground flex items-center gap-2">
            <Megaphone size={16} />
            Promotional Banner
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-muted-foreground text-sm font-light leading-relaxed">
            Display a promotional banner at the top of the homepage and services page. Great for announcing sales, specials, or new offerings.
          </p>
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-4 border border-border/50 bg-background">
            <div className="min-w-0">
              <p className="text-sm font-medium">Show Banner</p>
              <p className="text-xs text-muted-foreground mt-0.5">
                {promoBannerEnabled
                  ? "Banner is visible on the homepage and services page"
                  : "Banner is hidden from all pages"}
              </p>
            </div>
            <Switch checked={promoBannerEnabled} onCheckedChange={setPromoBannerEnabled} className="shrink-0" />
          </div>
          {promoBannerEnabled && (
            <div className="space-y-4">
              <div>
                <Label className="text-xs tracking-wide uppercase text-muted-foreground">Title</Label>
                <Input
                  value={promoBannerTitle}
                  onChange={(e) => setPromoBannerTitle(e.target.value)}
                  placeholder="New Suite Special!"
                  className="bg-background border-border/50 mt-1"
                />
              </div>
              <div>
                <Label className="text-xs tracking-wide uppercase text-muted-foreground">Subtitle</Label>
                <Input
                  value={promoBannerSubtitle}
                  onChange={(e) => setPromoBannerSubtitle(e.target.value)}
                  placeholder="Any Medium or Long fullset"
                  className="bg-background border-border/50 mt-1"
                />
              </div>
              <div>
                <Label className="text-xs tracking-wide uppercase text-muted-foreground">Detail (price/duration)</Label>
                <Input
                  value={promoBannerDetail}
                  onChange={(e) => setPromoBannerDetail(e.target.value)}
                  placeholder="1 hour 30 minutes @ $75.00"
                  className="bg-background border-border/50 mt-1"
                />
              </div>
              <div>
                <Label className="text-xs tracking-wide uppercase text-muted-foreground">Link</Label>
                <Input
                  value={promoBannerLink}
                  onChange={(e) => setPromoBannerLink(e.target.value)}
                  placeholder="/book"
                  className="bg-background border-border/50 mt-1"
                />
                <p className="text-xs text-muted-foreground mt-1">Where clicking the banner takes the user (e.g. /book, /services)</p>
              </div>
            </div>
          )}
          <Button onClick={handleSave} disabled={saving} className="text-xs tracking-widest uppercase">
            <Save size={14} className="mr-2" />
            {saving ? "Saving…" : "Save Settings"}
          </Button>
        </CardContent>
      </Card>


      <Card className="bg-card border-border/50 max-w-xl">
        <CardHeader>
          <CardTitle className="text-sm font-light tracking-[0.3em] uppercase text-muted-foreground flex items-center gap-2">
            <CreditCard size={16} />
            Deposit Settings
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-muted-foreground text-sm font-light leading-relaxed">
            Configure whether a deposit is required when clients book, and how much they need to pay upfront.
          </p>
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-4 border border-border/50 bg-background">
            <div className="min-w-0">
              <p className="text-sm font-medium">Require Deposit</p>
              <p className="text-xs text-muted-foreground mt-0.5">
                {depositEnabled
                  ? "Clients must pay a deposit to secure their booking"
                  : "No deposit required — clients pay in full at appointment"}
              </p>
            </div>
            <Switch
              checked={depositEnabled}
              onCheckedChange={setDepositEnabled}
              className="shrink-0"
            />
          </div>
          {depositEnabled && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label className="text-xs tracking-wide uppercase text-muted-foreground">Deposit Type</Label>
                <Select value={depositType} onValueChange={setDepositType}>
                  <SelectTrigger className="bg-background border-border/50 mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="fixed">Fixed Amount ($)</SelectItem>
                    <SelectItem value="percentage">Percentage (%)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs tracking-wide uppercase text-muted-foreground">
                  {depositType === "fixed" ? "Amount ($)" : "Percentage (%)"}
                </Label>
                <Input
                  type="number"
                  value={depositAmount}
                  onChange={(e) => setDepositAmount(e.target.value)}
                  placeholder={depositType === "fixed" ? "15" : "50"}
                  min="1"
                  max={depositType === "percentage" ? "100" : undefined}
                  className="bg-background border-border/50 mt-1"
                />
              </div>
            </div>
          )}
          <p className="text-xs text-muted-foreground">
            {!depositEnabled
              ? "Deposits are disabled. Clients will pay in person."
              : depositType === "fixed"
                ? `Clients will pay a $${depositAmount || 0} deposit to secure their booking.`
                : `Clients will pay ${depositAmount || 0}% of the service price as a deposit.`}
          </p>
          <Button onClick={handleSave} disabled={saving} className="text-xs tracking-widest uppercase">
            <Save size={14} className="mr-2" />
            {saving ? "Saving…" : "Save Settings"}
          </Button>
        </CardContent>
      </Card>

      {/* Client Check-In */}
      <Card className="bg-card border-border/50 max-w-xl">
        <CardHeader>
          <CardTitle className="text-sm font-light tracking-[0.3em] uppercase text-muted-foreground flex items-center gap-2 flex-wrap">
            <CheckCircle size={16} />
            <span>Client Check-In</span>
            <Badge
              variant="outline"
              className={`ml-auto normal-case tracking-normal text-[10px] font-medium ${
                checkInEnabled
                  ? "bg-emerald-500/15 text-emerald-700 dark:text-emerald-400 border-emerald-500/30"
                  : "bg-muted text-muted-foreground border-border/50"
              }`}
            >
              {checkInEnabled ? "On" : "Off"}
            </Badge>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-5">
          <p className="text-muted-foreground text-sm font-light leading-relaxed">
            Controls the <span className="font-medium text-foreground">Check In</span> buttons on the admin dashboard and bookings list. Turn off to hide check-in entirely.
          </p>
          <div className="flex items-start justify-between gap-3 p-3 border border-border/50 rounded-md bg-background">
            <div>
              <p className="text-sm font-medium">Enable check-in</p>
              <p className="text-xs text-muted-foreground mt-0.5">
                When off, the Check In buttons and arrival timestamps are hidden from admin views.
              </p>
            </div>
            <Switch checked={checkInEnabled} onCheckedChange={setCheckInEnabled} className="shrink-0" />
          </div>
          <Button onClick={handleSave} disabled={saving} className="text-xs tracking-widest uppercase">
            <Save size={14} className="mr-2" />
            {saving ? "Saving…" : "Save Settings"}
          </Button>
        </CardContent>
      </Card>

      {/* Lookbook */}
      <LookbookManagerCard />

      {/* Auto-Block Next Month */}
      <Card className="bg-card border-border/50 max-w-xl">
        <CardHeader>
          <CardTitle className="text-sm font-light tracking-[0.3em] uppercase text-muted-foreground flex items-center gap-2 flex-wrap">
            <CalendarOff size={16} />
            <span>Auto-Block Next Month</span>
            {autoBlockStatus && (
              autoBlockStatus.blockedCount > 0 ? (
                <Badge variant="outline" className="ml-auto normal-case tracking-normal text-[10px] font-medium bg-amber-500/15 text-amber-700 dark:text-amber-400 border-amber-500/30">
                  {autoBlockStatus.monthLabel} · {autoBlockStatus.blockedCount}/{autoBlockStatus.daysInNextMonth} days blocked
                </Badge>
              ) : (
                <Badge variant="outline" className="ml-auto normal-case tracking-normal text-[10px] font-medium bg-emerald-500/15 text-emerald-700 dark:text-emerald-400 border-emerald-500/30">
                  {autoBlockStatus.monthLabel} · open for booking
                </Badge>
              )
            )}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-5">
          <p className="text-muted-foreground text-sm font-light leading-relaxed">
            Automatically block all of <span className="font-medium text-foreground">next month</span> from booking until the day below in the current month — when bookings open up. Runs daily at 12:05&nbsp;AM UTC.
          </p>

          <div className="flex items-start justify-between gap-3 p-3 border border-border/50 rounded-md bg-background">
            <div>
              <p className="text-sm font-medium">Enable auto-block</p>
              <p className="text-xs text-muted-foreground mt-0.5">
                When on, next month is auto-blocked until the unlock day.
              </p>
            </div>
            <Switch checked={autoBlockEnabled} onCheckedChange={setAutoBlockEnabled} className="shrink-0" />
          </div>

          <div className="space-y-2">
            <Label htmlFor="unlock-day" className="text-xs uppercase tracking-widest text-muted-foreground">
              Unlock day of month
            </Label>
            <Input
              id="unlock-day"
              type="number"
              min={1}
              max={28}
              value={autoBlockUnlockDay}
              onChange={(e) => setAutoBlockUnlockDay(e.target.value)}
              className="max-w-[120px]"
            />
            <p className="text-xs text-muted-foreground">
              On day <span className="font-medium text-foreground">{autoBlockUnlockDay || 22}</span> of each month, next month opens for booking. (1–28)
            </p>
          </div>

          <div className="flex flex-wrap gap-2">
            <Button
              onClick={handleSave}
              disabled={saving}
              className="text-xs tracking-widest uppercase"
            >
              <Save size={14} className="mr-2" />
              {saving ? "Saving…" : "Save"}
            </Button>
            <Button
              variant="outline"
              onClick={() => runAutoBlock("block")}
              disabled={autoBlockRunning !== "none"}
              className="text-xs tracking-widest uppercase"
            >
              {autoBlockRunning === "block" ? <Loader2 size={14} className="mr-2 animate-spin" /> : <CalendarOff size={14} className="mr-2" />}
              Block Next Month Now
            </Button>
            <Button
              variant="outline"
              onClick={() => runAutoBlock("unblock")}
              disabled={autoBlockRunning !== "none"}
              className="text-xs tracking-widest uppercase"
            >
              {autoBlockRunning === "unblock" ? <Loader2 size={14} className="mr-2 animate-spin" /> : <CheckCircle size={14} className="mr-2" />}
              Unblock Next Month Now
            </Button>
          </div>
          <p className="text-[11px] text-muted-foreground">
            Manual runs only affect blocks tagged <code className="font-mono">AUTO_BLOCK</code> — your hand-made blocks are untouched.
          </p>
        </CardContent>
      </Card>

      {/* Walk-in / Existing Client Checkout */}
      <WalkInCheckoutCard />

      {/* Resend missed booking notifications */}
      <ResendNotificationsCard />

      {/* Payment Portal */}
      <Card id="payment-portal" className="bg-card border-border/50 max-w-xl scroll-mt-24">
        <CardHeader>
          <CardTitle className="text-sm font-light tracking-[0.3em] uppercase text-muted-foreground flex items-center gap-2">
            <Wallet size={16} />
            Payment Portal
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-5">
          <p className="text-muted-foreground text-sm font-light leading-relaxed">
            Configure how payments are collected from clients at appointment time. Use the <span className="font-medium text-foreground">Collect Payment</span> button on any upcoming booking.
          </p>

          {/* Stripe status */}
          <div className="flex items-center justify-between p-3 border border-border/50 rounded-md bg-background">
            <div className="flex items-center gap-2">
              <CreditCard size={16} className="text-primary" />
              <div>
                <p className="text-sm font-medium">Stripe</p>
                <p className="text-[11px] text-muted-foreground">Online card payments via secure checkout link</p>
              </div>
            </div>
            <span className="text-[10px] px-2 py-1 rounded-full bg-emerald-500/15 text-emerald-600 font-medium tracking-wide">
              CONNECTED
            </span>
          </div>

          {/* Tap to Pay status */}
          {acceptedMethods.includes("tap_to_pay") && (
            <div className="p-3 border border-primary/30 rounded-md bg-primary/5 space-y-2">
              <div className="flex items-center gap-2">
                <Smartphone size={16} className="text-primary" />
                <p className="text-sm font-medium">Tap to Pay (Stripe Terminal)</p>
                <span className="text-[10px] px-2 py-0.5 rounded-full bg-amber-500/15 text-amber-600 font-medium tracking-wide ml-auto">
                  NATIVE APP REQUIRED
                </span>
              </div>
              <p className="text-[11px] text-muted-foreground leading-relaxed">
                Backend is wired up. To accept actual NFC card taps, install this app on a compatible device:
              </p>
              <ul className="text-[11px] text-muted-foreground list-disc list-inside space-y-0.5 ml-1">
                <li>iPhone XS or newer (iOS 16.4+) — uses Tap to Pay on iPhone</li>
                <li>Android phone with NFC (Android 11+)</li>
                <li>Stripe account approved for Tap to Pay (already done ✓)</li>
                <li>Run <code className="px-1 py-0.5 bg-background border border-border/50 rounded text-[10px]">npx cap sync</code> and add the Stripe Terminal SDK in Xcode/Android Studio</li>
              </ul>
            </div>
          )}

          {/* Accepted methods */}
          <div>
            <Label className="text-xs tracking-wide uppercase text-muted-foreground">Accepted Methods</Label>
            <p className="text-[11px] text-muted-foreground mt-1 mb-2">Toggle which payment options appear in the Collect Payment dialog.</p>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              {ALL_METHODS.map((m) => {
                const enabled = acceptedMethods.includes(m.value);
                return (
                  <button
                    type="button"
                    key={m.value}
                    onClick={() => {
                      setAcceptedMethods((prev) =>
                        enabled ? prev.filter((x) => x !== m.value) : [...prev, m.value]
                      );
                    }}
                    className={cn(
                      "flex items-center gap-2 p-2.5 rounded-md border text-xs transition-all",
                      enabled
                        ? "border-primary bg-primary/10 text-primary"
                        : "border-border/50 text-muted-foreground hover:border-primary/40"
                    )}
                  >
                    <m.icon size={14} />
                    {m.label}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Tip presets */}
          <div>
            <Label className="text-xs tracking-wide uppercase text-muted-foreground">Tip Presets (%)</Label>
            <Input
              value={tipPresets}
              onChange={(e) => setTipPresets(e.target.value)}
              placeholder="15,20,25"
              className="bg-background border-border/50 mt-1"
            />
            <p className="text-[11px] text-muted-foreground mt-1">Comma-separated percentages shown as quick tip buttons.</p>
          </div>

          {/* Tax rate */}
          <div>
            <Label className="text-xs tracking-wide uppercase text-muted-foreground">Sales Tax (%)</Label>
            <Input
              type="number"
              step="0.01"
              min="0"
              max="100"
              value={taxRate}
              onChange={(e) => setTaxRate(e.target.value)}
              placeholder="0"
              className="bg-background border-border/50 mt-1"
            />
            <p className="text-[11px] text-muted-foreground mt-1">Set to 0 to skip tax. Otherwise auto-calculated on the collect screen.</p>
          </div>

          <Button onClick={handleSave} disabled={saving} className="text-xs tracking-widest uppercase">
            <Save size={14} className="mr-2" />
            {saving ? "Saving…" : "Save Payment Settings"}
          </Button>

          {/* Recent manual payments log */}
          {recentManualPayments.length > 0 && (
            <div className="pt-4 border-t border-border/30">
              <Label className="text-xs tracking-wide uppercase text-muted-foreground flex items-center gap-1.5">
                <Receipt size={12} /> Recent Collections
              </Label>
              <div className="mt-2 space-y-1.5 max-h-72 overflow-y-auto">
                {recentManualPayments.map((p) => (
                  <div key={p.id} className="flex items-center justify-between gap-3 p-2.5 border border-border/40 rounded-md bg-background text-xs">
                    <div className="min-w-0 flex-1">
                      <p className="font-medium truncate">{manualPayProfiles[p.user_id] || "Unknown"}</p>
                      <p className="text-[10px] text-muted-foreground">
                        {new Date(p.created_at).toLocaleString()} · <span className="uppercase tracking-wide">{p.method}</span>
                      </p>
                    </div>
                    <div className="text-right shrink-0">
                      <p className="font-bold text-primary">${Number(p.total_amount).toFixed(2)}</p>
                      {Number(p.tip_amount) > 0 && (
                        <p className="text-[10px] text-muted-foreground">tip ${Number(p.tip_amount).toFixed(2)}</p>
                      )}
                    </div>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7 shrink-0 text-muted-foreground hover:text-destructive"
                      onClick={() => deleteManualPayment(p.id)}
                      aria-label="Delete payment"
                    >
                      <Trash2 size={13} />
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </CardContent>
      </Card>



      <Card className="bg-card border-border/50 max-w-xl">
        <CardHeader>
          <CardTitle className="text-sm font-light tracking-[0.3em] uppercase text-muted-foreground flex items-center gap-2">
            <Phone size={16} />
            Phone Verification
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-muted-foreground text-sm font-light leading-relaxed">
            When enabled, new clients must verify their phone number via SMS code during sign-up. Disable this to skip the verification step.
          </p>
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-4 border border-border/50 bg-background">
            <div className="min-w-0">
              <p className="text-sm font-medium">Phone Number Required</p>
              <p className="text-xs text-muted-foreground mt-0.5">
                {phoneRequired
                  ? "Clients must enter a phone number to sign up"
                  : "Phone number is optional at sign-up"}
              </p>
            </div>
            <Switch
              checked={phoneRequired}
              onCheckedChange={setPhoneRequired}
              className="shrink-0"
            />
          </div>
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-4 border border-border/50 bg-background">
            <div className="min-w-0">
              <p className="text-sm font-medium">Require Phone Verification</p>
              <p className="text-xs text-muted-foreground mt-0.5">
                {phoneVerificationEnabled
                  ? "Clients must verify phone via OTP to sign up"
                  : "Phone verification is disabled — clients can sign up without SMS code"}
              </p>
              {!phoneRequired && phoneVerificationEnabled && (
                <p className="text-xs text-amber-600 mt-1">
                  Verification only runs when a phone number is provided.
                </p>
              )}
            </div>
            <Switch
              checked={phoneVerificationEnabled}
              onCheckedChange={setPhoneVerificationEnabled}
              className="shrink-0"
            />
          </div>
          <Button onClick={handleSave} disabled={saving} className="text-xs tracking-widest uppercase">
            <Save size={14} className="mr-2" />
            {saving ? "Saving…" : "Save Settings"}
          </Button>
        </CardContent>
      </Card>

      <Card className="bg-card border-border/50 max-w-xl">
        <CardHeader>
          <CardTitle className="text-sm font-light tracking-[0.3em] uppercase text-muted-foreground flex items-center gap-2">
            <Mail size={16} />
            Email Confirmation
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-muted-foreground text-sm font-light leading-relaxed">
            When enabled, new clients who provide an email address must verify it before they can sign in. Phone-only accounts are not affected.
          </p>
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-4 border border-border/50 bg-background">
            <div className="min-w-0">
              <p className="text-sm font-medium">Require Email Confirmation</p>
              <p className="text-xs text-muted-foreground mt-0.5">
                {emailConfirmationEnabled
                  ? "Clients must click the link in their email to activate their account"
                  : "Accounts are activated immediately — no email verification needed"}
              </p>
            </div>
            <Switch
              checked={emailConfirmationEnabled}
              onCheckedChange={setEmailConfirmationEnabled}
              className="shrink-0"
            />
          </div>
          <Button onClick={handleSave} disabled={saving} className="text-xs tracking-widest uppercase">
            <Save size={14} className="mr-2" />
            {saving ? "Saving…" : "Save Settings"}
          </Button>
        </CardContent>
      </Card>

      <Card className="bg-card border-border/50 max-w-xl">
        <CardHeader>
          <CardTitle className="text-sm font-light tracking-[0.3em] uppercase text-muted-foreground flex items-center gap-2">
            <MessageSquare size={16} />
            Appointment Reminders
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-muted-foreground text-sm font-light leading-relaxed">
            Clients receive up to two reminders before their appointment — typically one the day before and one closer to the visit. You can toggle the second reminder off if you only want one.
          </p>
          <div>
            <Label className="text-xs tracking-wide uppercase text-muted-foreground">Reminder #1 — Send Before Appointment</Label>
            <Select value={reminderLeadMinutes} onValueChange={setReminderLeadMinutes}>
              <SelectTrigger className="bg-background border-border/50 mt-1">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="30">30 minutes before</SelectItem>
                <SelectItem value="60">1 hour before</SelectItem>
                <SelectItem value="120">2 hours before</SelectItem>
                <SelectItem value="180">3 hours before</SelectItem>
                <SelectItem value="360">6 hours before</SelectItem>
                <SelectItem value="720">12 hours before</SelectItem>
                <SelectItem value="1440">24 hours before</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="p-4 border border-border/50 bg-background space-y-3">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
              <div className="min-w-0">
                <p className="text-sm font-medium">Reminder #2</p>
                <p className="text-xs text-muted-foreground mt-0.5">
                  {reminder2Enabled
                    ? "A second reminder is sent closer to the appointment"
                    : "Only one reminder will be sent"}
                </p>
              </div>
              <Switch
                checked={reminder2Enabled}
                onCheckedChange={setReminder2Enabled}
                className="shrink-0"
              />
            </div>
            {reminder2Enabled && (
              <div>
                <Label className="text-xs tracking-wide uppercase text-muted-foreground">Reminder #2 — Send Before Appointment</Label>
                <Select value={reminderLeadMinutes2} onValueChange={setReminderLeadMinutes2}>
                  <SelectTrigger className="bg-background border-border/50 mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="30">30 minutes before</SelectItem>
                    <SelectItem value="60">1 hour before</SelectItem>
                    <SelectItem value="120">2 hours before</SelectItem>
                    <SelectItem value="180">3 hours before</SelectItem>
                    <SelectItem value="360">6 hours before</SelectItem>
                    <SelectItem value="720">12 hours before</SelectItem>
                    <SelectItem value="1440">24 hours before</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground mt-1">
                  Each reminder applies to both SMS and email channels.
                </p>
              </div>
            )}
          </div>
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-4 border border-border/50 bg-background">
            <div className="min-w-0">
              <p className="text-sm font-medium">Email Reminder</p>
              <p className="text-xs text-muted-foreground mt-0.5">
                {emailReminderEnabled
                  ? "Clients will also receive an email reminder (in addition to SMS)"
                  : "Only SMS reminders are sent"}
              </p>
            </div>
            <Switch
              checked={emailReminderEnabled}
              onCheckedChange={setEmailReminderEnabled}
              className="shrink-0"
            />
          </div>
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-4 border border-border/50 bg-background">
            <div className="min-w-0">
              <p className="text-sm font-medium">Click-to-Confirm</p>
              <p className="text-xs text-muted-foreground mt-0.5">
                {smsConfirmEnabled
                  ? "Clients will be asked to reply CONFIRM"
                  : "Reminders are informational only"}
              </p>
            </div>
            <Switch
              checked={smsConfirmEnabled}
              onCheckedChange={setSmsConfirmEnabled}
              className="shrink-0"
            />
          </div>
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-4 border border-border/50 bg-background">
            <div className="min-w-0">
              <p className="text-sm font-medium">Marketing Messages</p>
              <p className="text-xs text-muted-foreground mt-0.5">
                {smsMarketingEnabled
                  ? "Marketing texts are enabled for opted-in clients"
                  : "Marketing texts are disabled"}
              </p>
            </div>
            <Switch
              checked={smsMarketingEnabled}
              onCheckedChange={setSmsMarketingEnabled}
              className="shrink-0"
            />
          </div>
          <div className="p-4 border border-border/50 bg-background space-y-3">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
              <div className="min-w-0">
                <p className="text-sm font-medium">Outlook Calendar Sync</p>
                <p className="text-xs text-muted-foreground mt-0.5">
                  {outlookSyncEnabled
                    ? "Bookings will be sent as calendar invites to your Outlook"
                    : "Outlook calendar invites are disabled"}
                </p>
              </div>
              <Switch
                checked={outlookSyncEnabled}
                onCheckedChange={setOutlookSyncEnabled}
                className="shrink-0"
              />
            </div>
            {outlookSyncEnabled && (
              <div className="space-y-1">
                <Label htmlFor="outlook-sync-email" className="text-xs">Outlook email address</Label>
                <Input
                  id="outlook-sync-email"
                  type="email"
                  placeholder="you@outlook.com"
                  value={outlookSyncEmail}
                  onChange={(e) => setOutlookSyncEmail(e.target.value)}
                />
                <p className="text-[11px] text-muted-foreground">
                  Each new, updated, or cancelled booking will email a calendar invite (.ics) to this address. Outlook adds it to your calendar automatically.
                </p>
                <div className="pt-2">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={handleBulkOutlookSync}
                    disabled={outlookBulkSyncing}
                    className="text-xs tracking-widest uppercase"
                  >
                    {outlookBulkSyncing ? (
                      <Loader2 size={14} className="mr-2 animate-spin" />
                    ) : (
                      <Mail size={14} className="mr-2" />
                    )}
                    {outlookBulkSyncing
                      ? outlookBulkProgress
                        ? `Syncing ${outlookBulkProgress.done}/${outlookBulkProgress.total}…`
                        : "Syncing…"
                      : "Sync existing bookings now"}
                  </Button>
                  <p className="text-[11px] text-muted-foreground mt-1.5">
                    One-time push of all upcoming, non-cancelled bookings to your Outlook inbox as calendar invites. Save settings first.
                  </p>
                </div>
              </div>
            )}
          </div>
          <Button onClick={handleSave} disabled={saving} className="text-xs tracking-widest uppercase">
            <Save size={14} className="mr-2" />
            {saving ? "Saving…" : "Save Settings"}
          </Button>
        </CardContent>
      </Card>

      <Card className="bg-card border-border/50 max-w-xl">
        <CardHeader>
          <CardTitle className="text-sm font-light tracking-[0.3em] uppercase text-muted-foreground flex items-center gap-2">
            <Camera size={16} />
            Client Photo Requirements
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-muted-foreground text-sm font-light leading-relaxed">
            Configure when clients are required to upload photos (hands, feet, face). Photos help you assess nails before appointments.
          </p>

          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-4 border border-border/50 bg-background">
            <div className="min-w-0">
              <p className="text-sm font-medium">Require Photos — First Appointment</p>
              <p className="text-xs text-muted-foreground mt-0.5">
                {requirePhotosFirst
                  ? "New clients must upload photos before their first booking"
                  : "No photos required for first-time clients"}
              </p>
            </div>
            <Switch checked={requirePhotosFirst} onCheckedChange={setRequirePhotosFirst} className="shrink-0" />
          </div>

          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-4 border border-border/50 bg-background">
            <div className="min-w-0">
              <p className="text-sm font-medium">Require Photos — Subsequent Appointments</p>
              <p className="text-xs text-muted-foreground mt-0.5">
                {requirePhotosSubsequent
                  ? "Returning clients must have photos on file for each booking"
                  : "No photos required for returning clients"}
              </p>
            </div>
            <Switch checked={requirePhotosSubsequent} onCheckedChange={setRequirePhotosSubsequent} className="shrink-0" />
          </div>

          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-4 border border-border/50 bg-background">
            <div className="min-w-0">
              <p className="text-sm font-medium">Require Pedicure Photo</p>
              <p className="text-xs text-muted-foreground mt-0.5">
                {requirePedicurePhoto
                  ? "Clients must upload a feet photo specifically for pedicure bookings"
                  : "No extra photo required for pedicure services"}
              </p>
            </div>
            <Switch checked={requirePedicurePhoto} onCheckedChange={setRequirePedicurePhoto} className="shrink-0" />
          </div>

          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-4 border border-border/50 bg-background">
            <div className="min-w-0">
              <p className="text-sm font-medium">Prompt Completion Photo at Payment</p>
              <p className="text-xs text-muted-foreground mt-0.5">
                {requireCompletionPhoto
                  ? "Admin will be prompted to upload a completion photo when finalizing a booking"
                  : "No completion photo prompt at payment"}
              </p>
            </div>
            <Switch checked={requireCompletionPhoto} onCheckedChange={setRequireCompletionPhoto} className="shrink-0" />
          </div>

          <div>
            <Label className="text-xs tracking-wide uppercase text-muted-foreground">Enforcement Mode</Label>
            <Select value={photoEnforcementMode} onValueChange={(v) => setPhotoEnforcementMode(v as "block" | "warn")}>
              <SelectTrigger className="bg-background border-border/50 mt-1">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="warn">Show Warning (allow booking)</SelectItem>
                <SelectItem value="block">Block Booking (require photos)</SelectItem>
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground mt-1">
              {photoEnforcementMode === "block"
                ? "Clients cannot proceed without uploading required photos"
                : "Clients see a warning but can still book without photos"}
            </p>
          </div>

          <Button onClick={handleSave} disabled={saving} className="text-xs tracking-widest uppercase">
            <Save size={14} className="mr-2" />
            {saving ? "Saving…" : "Save Settings"}
          </Button>
        </CardContent>
      </Card>

      {/* Coupon Expiry Reminders */}
      <Card className="bg-card border-border/50 max-w-xl">
        <CardHeader>
          <CardTitle className="text-sm font-light tracking-[0.3em] uppercase text-muted-foreground flex items-center gap-2">
            <Mail size={16} />
            Coupon Expiry Reminders
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-4 border border-border/50 bg-background">
            <div className="min-w-0">
              <p className="text-sm font-medium">Enable Expiry Reminders</p>
              <p className="text-xs text-muted-foreground mt-0.5">
                {couponExpiryReminderEnabled
                  ? "Clients will receive an email when their coupon is about to expire"
                  : "No expiry reminder emails will be sent"}
              </p>
            </div>
            <Switch checked={couponExpiryReminderEnabled} onCheckedChange={setCouponExpiryReminderEnabled} className="shrink-0" />
          </div>
          {couponExpiryReminderEnabled && (
            <div className="space-y-2">
              <Label className="text-xs tracking-wide uppercase text-muted-foreground">Remind before expiry (days)</Label>
              <Select value={couponExpiryReminderDays} onValueChange={setCouponExpiryReminderDays}>
                <SelectTrigger className="bg-background border-border/50">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="7">7 days before</SelectItem>
                  <SelectItem value="14">14 days before</SelectItem>
                  <SelectItem value="30">30 days before (default)</SelectItem>
                  <SelectItem value="45">45 days before</SelectItem>
                  <SelectItem value="60">60 days before</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                A single reminder email is sent to each client when their unused coupon falls within this window.
              </p>
            </div>
          )}
          <Button onClick={handleSave} disabled={saving} className="text-xs tracking-widest uppercase">
            <Save size={14} className="mr-2" />
            {saving ? "Saving…" : "Save Settings"}
          </Button>
        </CardContent>
      </Card>



      {/* Instagram-related settings have moved to /admin/rewards */}

      <Collapsible open={advancedOpen} onOpenChange={setAdvancedOpen} className="max-w-xl">
        <CollapsibleTrigger asChild>
          <button className="flex items-center justify-between w-full px-4 py-3 rounded-lg border border-border/50 bg-card hover:bg-accent/50 transition-colors">
            <div className="flex items-center gap-2">
              <Settings2 size={16} className="text-muted-foreground" />
              <span className="text-sm font-light tracking-[0.3em] uppercase text-muted-foreground">Advanced Settings</span>
            </div>
            <ChevronDown size={16} className={cn("text-muted-foreground transition-transform", advancedOpen && "rotate-180")} />
          </button>
        </CollapsibleTrigger>
        <CollapsibleContent className="space-y-6 mt-6">
          {/* Allow clients to delete past appointments */}
          <Card className="bg-card border-border/50">
            <CardHeader>
              <CardTitle className="text-sm font-light tracking-[0.3em] uppercase text-muted-foreground flex items-center gap-2">
                <Trash2 size={16} />
                Delete Past Appointments
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-muted-foreground text-sm font-light leading-relaxed">
                When enabled, a Delete button appears on completed and cancelled bookings in the admin Bookings page.
                Deleting permanently removes the appointment along with all associated photos, add-ons, and reviews. This cannot be undone.
              </p>
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-4 border border-border/50 bg-background">
                <div className="min-w-0">
                  <p className="text-sm font-medium">Enable Admin Delete</p>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    {allowAdminDeletePastBookings
                      ? "Admins can permanently delete past appointments"
                      : "Past appointments are protected from deletion"}
                  </p>
                </div>
                <Switch
                  checked={allowAdminDeletePastBookings}
                  onCheckedChange={setAllowAdminDeletePastBookings}
                  className="shrink-0"
                />
              </div>
              <Button onClick={handleSave} disabled={saving} className="text-xs tracking-widest uppercase">
                <Save size={14} className="mr-2" />
                {saving ? "Saving…" : "Save Settings"}
              </Button>
            </CardContent>
          </Card>

          {/* Pending banner filters */}
          <Card className="bg-card border-border/50">
            <CardHeader>
              <CardTitle className="text-sm font-light tracking-[0.3em] uppercase text-muted-foreground flex items-center gap-2">
                <Bell size={16} />
                Dashboard Pending Banner
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-muted-foreground text-sm font-light leading-relaxed">
                Control which pending items count toward the slow-flashing banner on the admin dashboard
                (reschedules, IG credit approvals, and add-on requests).
              </p>

              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-4 border border-border/50 bg-background">
                <div className="min-w-0">
                  <p className="text-sm font-medium">Exclude test accounts</p>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    {bannerExcludeTestAccounts
                      ? "Pending items from test/admin accounts are hidden from the banner counts"
                      : "All pending items count, including those from test accounts"}
                  </p>
                </div>
                <Switch
                  checked={bannerExcludeTestAccounts}
                  onCheckedChange={setBannerExcludeTestAccounts}
                  className="shrink-0"
                />
              </div>

              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-4 border border-border/50 bg-background">
                <div className="min-w-0">
                  <p className="text-sm font-medium">Exclude inactive bookings</p>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    {bannerExcludeInactiveBookings
                      ? "Reschedule & add-on requests for cancelled or past bookings are excluded"
                      : "All pending requests count, even for cancelled or past bookings"}
                  </p>
                </div>
                <Switch
                  checked={bannerExcludeInactiveBookings}
                  onCheckedChange={setBannerExcludeInactiveBookings}
                  className="shrink-0"
                />
              </div>

              <Button onClick={handleSave} disabled={saving} className="text-xs tracking-widest uppercase">
                <Save size={14} className="mr-2" />
                {saving ? "Saving…" : "Save Settings"}
              </Button>
            </CardContent>
          </Card>

          {/* Send Test SMS */}
          <Card className="bg-card border-border/50">
            <CardHeader>
              <CardTitle className="text-sm font-light tracking-[0.3em] uppercase text-muted-foreground flex items-center gap-2">
                <MessageSquare size={16} />
                Send Test SMS
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-muted-foreground text-sm font-light leading-relaxed">
                Trigger the booking SMS edge function with sample data to verify Twilio delivery. Admin only.
              </p>
              <div className="grid sm:grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs tracking-wide uppercase text-muted-foreground">Destination Phone</Label>
                  <Input
                    value={testSmsPhone}
                    onChange={(e) => setTestSmsPhone(e.target.value)}
                    placeholder="+16144040458"
                    className="bg-background border-border/50 mt-1"
                  />
                </div>
                <div>
                  <Label className="text-xs tracking-wide uppercase text-muted-foreground">Message Type</Label>
                  <Select value={testSmsType} onValueChange={(v: any) => setTestSmsType(v)}>
                    <SelectTrigger className="bg-background border-border/50 mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="confirmation">Confirmation</SelectItem>
                      <SelectItem value="completion">Completion</SelectItem>
                      <SelectItem value="cancellation">Cancellation</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <Button
                onClick={async () => {
                  const cleaned = testSmsPhone.trim();
                  if (!cleaned) {
                    toast({ title: "Phone required", description: "Enter a destination phone number.", variant: "destructive" });
                    return;
                  }
                  setTestSmsSending(true);
                  setTestSmsResult(null);
                  try {
                    const { data, error } = await supabase.functions.invoke("send-booking-sms", {
                      body: {
                        type: testSmsType,
                        phone: cleaned,
                        service_name: "Test Booking",
                        booking_date: new Date().toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric", year: "numeric" }),
                        booking_time: "2:00 PM",
                        duration: "1 hr",
                        price: 50,
                      },
                    });
                    if (error) throw error;
                    if ((data as any)?.error) throw new Error((data as any).error);
                    const sid = (data as any)?.sid;
                    const status = (data as any)?.status || "queued";
                    setTestSmsResult({ sid, status, phone: cleaned });
                    toast({ title: "Test SMS sent", description: `${status} → ${cleaned}` });

                    if (sid) {
                      let attempts = 0;
                      const poll = async () => {
                        attempts++;
                        try {
                          const { data: s } = await supabase.functions.invoke("twilio-message-status", { body: { sid } });
                          if (s && !(s as any).error) {
                            const newStatus = (s as any).status;
                            setTestSmsResult((prev) => prev && prev.sid === sid ? { ...prev, status: newStatus, errorMessage: (s as any).error_message } : prev);
                            if (["delivered", "failed", "undelivered"].includes(newStatus) || attempts >= 10) return;
                          }
                        } catch {}
                        if (attempts < 10) setTimeout(poll, 3000);
                      };
                      setTimeout(poll, 2500);
                    }
                  } catch (err: any) {
                    toast({ title: "SMS failed", description: err?.message || "Unable to send test SMS", variant: "destructive" });
                    setTestSmsResult({ sid: "", status: "failed", phone: cleaned, errorMessage: err?.message });
                  } finally {
                    setTestSmsSending(false);
                  }
                }}
                disabled={testSmsSending}
                className="text-xs tracking-widest uppercase"
              >
                {testSmsSending ? <Loader2 size={14} className="mr-2 animate-spin" /> : <MessageSquare size={14} className="mr-2" />}
                {testSmsSending ? "Sending…" : "Send Test SMS"}
              </Button>

              {testSmsResult && (
                <div className="p-3 border border-border/50 bg-background/50 space-y-2">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-xs tracking-wide uppercase text-muted-foreground">Status:</span>
                    <Badge
                      variant={
                        testSmsResult.status === "delivered" ? "default"
                          : testSmsResult.status === "failed" || testSmsResult.status === "undelivered" ? "destructive"
                          : "secondary"
                      }
                      className="text-xs uppercase tracking-wider"
                    >
                      {testSmsResult.status}
                    </Badge>
                    {!["delivered", "failed", "undelivered"].includes(testSmsResult.status) && (
                      <Loader2 size={12} className="animate-spin text-muted-foreground" />
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground font-light">To: {testSmsResult.phone}</p>
                  {testSmsResult.sid && (
                    <p className="text-xs text-muted-foreground font-mono break-all">SID: {testSmsResult.sid}</p>
                  )}
                  {testSmsResult.errorMessage && (
                    <p className="text-xs text-destructive">{testSmsResult.errorMessage}</p>
                  )}
                </div>
              )}
            </CardContent>
          </Card>

          <Card className="bg-card border-border/50">
            <CardHeader>
              <CardTitle className="text-sm font-light tracking-[0.3em] uppercase text-muted-foreground flex items-center gap-2">
                <Code size={16} />
                Instagram Gallery
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-muted-foreground text-sm font-light leading-relaxed">
                The homepage gallery pulls your latest Instagram posts from Behold. Create a free feed at{" "}
                <a href="https://behold.so" target="_blank" rel="noopener noreferrer" className="text-primary underline">behold.so</a>,
                connect your Instagram account, then paste the feed ID (or the full feed link) below.
              </p>
              <div>
                <Label className="text-xs tracking-wide uppercase text-muted-foreground">Behold Feed ID or Link</Label>
                <Input
                  value={beholdFeedId}
                  onChange={(e) => {
                    const raw = e.target.value.trim();
                    const match = raw.match(/feeds\.behold\.so\/([A-Za-z0-9_-]+)/i);
                    setBeholdFeedId(match ? match[1] : raw);
                  }}
                  placeholder="e.g. aBcD1234 or https://feeds.behold.so/aBcD1234"
                  className="bg-background border-border/50 mt-1"
                />
                <p className="text-xs text-muted-foreground mt-2">
                  {beholdFeedId
                    ? "Saved posts will show on the homepage gallery."
                    : "Until a feed ID is added, the old gallery embed keeps showing."}
                </p>
              </div>

              {beholdFeedId && (
                <div className="space-y-2">
                  <Label className="text-xs tracking-wide uppercase text-muted-foreground">Live Preview</Label>
                  <div className="p-4 border border-border/50 bg-background/50">
                    <BeholdFeed feedId={beholdFeedId} limit={8} />
                  </div>
                </div>
              )}

              <Button onClick={handleSave} disabled={saving} className="text-xs tracking-widest uppercase">
                <Save size={14} className="mr-2" />
                {saving ? "Saving…" : "Save Settings"}
              </Button>
            </CardContent>
          </Card>


          {/* Liquid Glass Effect */}
          <Card className="bg-card border-border/50">
            <CardHeader>
              <CardTitle className="text-sm font-light tracking-[0.3em] uppercase text-muted-foreground flex items-center gap-2">
                <Sparkles size={16} />
                Liquid Glass Effect
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-muted-foreground text-sm font-light leading-relaxed">
                Enable Apple-style liquid glass styling on all buttons across the site. Adds frosted glass transparency, blur effects, and subtle depth.
              </p>
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-4 border border-border/50 bg-background">
                <div className="min-w-0">
                  <p className="text-sm font-medium">Enable Liquid Glass</p>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    {liquidGlassEnabled
                      ? "Buttons have frosted glass styling"
                      : "Buttons use standard solid styling"}
                  </p>
                </div>
                <Switch
                  checked={liquidGlassEnabled}
                  onCheckedChange={setLiquidGlassEnabled}
                  className="shrink-0"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-xs tracking-wide uppercase text-muted-foreground">Shimmer Loop Speed (seconds)</Label>
                <Input
                  type="number"
                  value={shimmerSpeed}
                  onChange={(e) => setShimmerSpeed(e.target.value)}
                  placeholder="2"
                  min="0.5"
                  max="10"
                  step="0.5"
                  className="bg-background border-border/50"
                />
                <p className="text-xs text-muted-foreground">
                  Current: {shimmerSpeed}s loop, {(parseFloat(shimmerSpeed) / 2).toFixed(1)}s offset between elements
                </p>
              </div>
              <div className="space-y-2">
                <Label className="text-xs tracking-wide uppercase text-muted-foreground">Shimmer Direction</Label>
                <Select value={shimmerDirection} onValueChange={setShimmerDirection}>
                  <SelectTrigger className="bg-background border-border/50">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="vertical">Vertical (top to bottom)</SelectItem>
                    <SelectItem value="horizontal">Horizontal (left to right)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <Button onClick={handleSave} disabled={saving} className="text-xs tracking-widest uppercase">
                <Save size={14} className="mr-2" />
                {saving ? "Saving…" : "Save Settings"}
              </Button>
            </CardContent>
          </Card>

          {/* Camera AI Detection */}
          <Card className="bg-card border-border/50">
            <CardHeader>
              <CardTitle className="text-sm font-light tracking-[0.3em] uppercase text-muted-foreground flex items-center gap-2">
                <ScanEye size={16} />
                Camera AI Detection
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-muted-foreground text-sm font-light leading-relaxed">
                When enabled, the camera overlay uses AI vision to automatically detect when a client's hand/feet/face is properly positioned and auto-captures the photo. When disabled, clients use the manual shutter button.
              </p>
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-4 border border-border/50 bg-background">
                <div className="min-w-0">
                  <p className="text-sm font-medium">AI Auto-Detection</p>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    {cameraMlDetection
                      ? "Camera will auto-detect positioning and capture automatically"
                      : "Manual shutter button only — no AI detection"}
                  </p>
                </div>
                <Switch checked={cameraMlDetection} onCheckedChange={setCameraMlDetection} className="shrink-0" />
              </div>
              <Button onClick={handleSave} disabled={saving} className="text-xs tracking-widest uppercase">
                <Save size={14} className="mr-2" />
                {saving ? "Saving…" : "Save Settings"}
              </Button>
            </CardContent>
          </Card>
        </CollapsibleContent>
      </Collapsible>

      <Card className="bg-card border-border/50 max-w-xl">
        <CardHeader>
          <CardTitle className="text-sm font-light tracking-[0.3em] uppercase text-muted-foreground flex items-center gap-2">
            <KeyRound size={16} />
            Admin Credentials
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-muted-foreground text-sm font-light leading-relaxed">
            Update your admin login email and password. Current email: <strong className="text-foreground">{user?.email}</strong>
          </p>
          <div>
            <Label className="text-xs tracking-wide uppercase text-muted-foreground">New Email</Label>
            <Input
              type="email"
              value={newEmail}
              onChange={(e) => setNewEmail(e.target.value)}
              placeholder="newemail@example.com"
              className="bg-background border-border/50 mt-1"
            />
          </div>
          <div>
            <Label className="text-xs tracking-wide uppercase text-muted-foreground">New Password</Label>
            <Input
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              placeholder="••••••••"
              className="bg-background border-border/50 mt-1"
            />
          </div>
          <div>
            <Label className="text-xs tracking-wide uppercase text-muted-foreground">Confirm Password</Label>
            <Input
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="••••••••"
              className="bg-background border-border/50 mt-1"
            />
          </div>
          <Button
            onClick={async () => {
              if (!newEmail && !newPassword) {
                toast({ title: "Error", description: "Enter a new email or password", variant: "destructive" });
                return;
              }
              if (newPassword && newPassword !== confirmPassword) {
                toast({ title: "Error", description: "Passwords do not match", variant: "destructive" });
                return;
              }
              if (newPassword && newPassword.length < 6) {
                toast({ title: "Error", description: "Password must be at least 6 characters", variant: "destructive" });
                return;
              }
              setCredentialsSaving(true);
              try {
                const { data, error } = await supabase.functions.invoke("admin-auth", {
                  body: {
                    action: "update_credentials",
                    email: newEmail || undefined,
                    password: newPassword || undefined,
                  },
                });
                if (error) throw error;
                if (data?.error) throw new Error(data.error);
                toast({ title: "Updated", description: "Credentials updated. You may need to sign in again with your new credentials." });
                setNewEmail("");
                setNewPassword("");
                setConfirmPassword("");
              } catch (err: any) {
                toast({ title: "Error", description: err.message, variant: "destructive" });
              }
              setCredentialsSaving(false);
            }}
            disabled={credentialsSaving}
            className="text-xs tracking-widest uppercase"
          >
            <Save size={14} className="mr-2" />
            {credentialsSaving ? "Updating…" : "Update Credentials"}
          </Button>
        </CardContent>
      </Card>

      {/* Feature Request Tracking - Super Admin Only */}
      {isSuperAdmin && (
        <Card className="bg-card border-border/50 max-w-xl">
          <CardHeader>
            <CardTitle className="text-sm font-light tracking-[0.3em] uppercase text-muted-foreground flex items-center gap-2">
              <Lightbulb size={16} />
              Feature Requests ({featureRequests.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {featureRequests.length === 0 ? (
              <p className="text-sm text-muted-foreground font-light">No feature requests yet.</p>
            ) : (
              featureRequests.map((req) => (
                <div key={req.id} className="border border-border/30 bg-background p-4 space-y-2">
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1">
                      <h4 className="text-sm font-semibold">{req.title}</h4>
                      {req.description && (
                        <p className="text-xs text-muted-foreground mt-1">{req.description}</p>
                      )}
                      <p className="text-[10px] text-muted-foreground mt-1.5">
                        By {featureProfiles[req.user_id] || "Unknown"} · {new Date(req.created_at).toLocaleDateString()}
                      </p>
                    </div>
                    <Badge
                      variant={req.status === "completed" ? "default" : req.status === "in_progress" ? "secondary" : req.status === "rejected" ? "destructive" : "outline"}
                      className="text-[10px] shrink-0"
                    >
                      {req.status === "in_progress" ? "In Progress" : req.status.charAt(0).toUpperCase() + req.status.slice(1)}
                    </Badge>
                  </div>
                  <div className="flex flex-wrap gap-1.5 pt-1">
                    <Button
                      size="sm"
                      variant="outline"
                      className="text-[10px] gap-1 h-7 px-2"
                      onClick={() => updateRequestStatus(req.id, "in_progress")}
                      disabled={req.status === "in_progress"}
                    >
                      <Clock size={10} /> In Progress
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      className="text-[10px] gap-1 h-7 px-2 border-green-500/30 text-green-700 hover:bg-green-500/10"
                      onClick={() => updateRequestStatus(req.id, "completed")}
                      disabled={req.status === "completed"}
                    >
                      <CheckCircle size={10} /> Complete
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      className="text-[10px] gap-1 h-7 px-2 border-destructive/30 text-destructive hover:bg-destructive/10"
                      onClick={() => updateRequestStatus(req.id, "rejected")}
                      disabled={req.status === "rejected"}
                    >
                      <XCircle size={10} /> Reject
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="text-[10px] gap-1 h-7 px-2 text-destructive"
                      onClick={() => deleteRequest(req.id)}
                    >
                      <Trash2 size={10} />
                    </Button>
                  </div>
                </div>
              ))
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
};

const BusinessHoursEditorButton = () => {
  const { bookingHours, refetch } = useAvailabilityData();
  return <AvailabilityHoursEditor bookingHours={bookingHours} onSave={refetch} />;
};

export default AdminSettings;
